import React, {
  useState,
  useEffect,
  SyntheticEvent,
  useCallback,
  useMemo,
  useContext,
  useRef,
  MutableRefObject,
} from 'react';
import cx from 'classnames';
import { formatInTimeZone } from 'date-fns-tz';
import { debounce, isFinite, isNumber } from 'lodash';
import { Slider, Typography } from '@mui/material';
import { trackEvent } from '@surfline/web-common';
import { Player } from 'components/CamPlayerKbygRewinds/usePlayerState/usePlayerState';
import WaveBarGraph from 'components/RewindGraph/WaveBarGraph/WaveBarGraph';
import { RewindsContext } from 'contexts/RewindsContext';
import useCalculateSliderPillMargin from 'hooks/useCalculateSliderPillMargin';
import useClientSize from 'hooks/useClientSize';
import type { Camera } from 'types/camera';
import type { SpotDetails } from 'types/spot';
import type { RewindError } from 'utils/rewinds/playlistBlocks.types';
import { GRAPH_BAR_WIDTH_IN_MS, WaveGraphBar } from 'utils/rewinds/waveInsightData';
import useCamAdRunning from 'utils/useGlobalAdRunningState';
import CamPlayerScrubberControls from 'components/CamPlayerScrubberControls/CamPlayerScrubberControls';
import { RefState } from 'hooks/useRefState';

import CamHostAd from 'components/CamHostAd/CamHostAd';
import theme from '@wavetrak/theme';
import { useUserPermissionStatus } from 'selectors/user';
import useCamControls from './useCamControls';
import styles from './CamPlayerRewindsScrubber.module.scss';

// the scrubber occupies 95% of the total width and the rewinds occupy 90% of the total width so the
// percentage representing the rewind bar is 90/95
const percentageRepresentingRewindBar = 90 / 95;
const halfSliderThumbWidth = 6;
const TIMELINE_VIRTUAL_SCRUB_HEIGHT = 30;

interface Props {
  bucketEndTimeInMs: number;
  bucketStartTimeInMs: number;
  camera: Camera;
  currentPlayer: Player;
  currentPreviewPlayer: Player;
  errors: RewindError[];
  hasInsights: boolean;
  hasRewinds: boolean;
  multiCamViewActive: boolean;
  onScrub: (isScrubbing: boolean) => void;
  playerTime?: number;
  playerWidth: number;
  setPlayerTime: (utcTimeToPlay: number, wantsRewindPlaying: boolean, forcePlay?: boolean) => void;
  setSliderTimeValue: (utcTimestamp: number) => void;
  showCamHostInRewind: boolean;
  showLive: () => void;
  showPreviewOfLive: () => void;
  showPreviewOfRewind: () => void;
  showRewind: () => void;
  showRewindGraph?: boolean;
  sliderTimeValue?: number | null;
  spot: SpotDetails;
  supportsSmartRewinds: boolean;
  timezone: string;
  updateCurrentPlaylist: (currentPlaylistTime: string) => void;
  userWantsRewindPlaying: boolean;
  waveGraphBars: WaveGraphBar[];
  waveUnits: string;
  playing: boolean;
  currentTimestampRef?: RefState<Date | undefined>;
  hasCurrentTimestamp: boolean;
  playerRef: HTMLElement | null;
  isFullscreen?: boolean;
  hasCamRewindPermissions: boolean;
  scrubberDayLabel?: string;
  isPreviewActive?: boolean;
  paywallActive: boolean;
  onTogglePlayPause: (event: Event | React.MouseEvent) => void;
}

const CamPlayerRewindsScrubber = ({
  bucketEndTimeInMs,
  bucketStartTimeInMs,
  camera,
  currentPlayer,
  currentPreviewPlayer,
  errors,
  hasInsights,
  hasRewinds,
  multiCamViewActive,
  onScrub,
  playerTime,
  playerWidth,
  setPlayerTime,
  setSliderTimeValue,
  showCamHostInRewind,
  showLive,
  showPreviewOfLive,
  showPreviewOfRewind,
  showRewind,
  showRewindGraph,
  sliderTimeValue,
  spot,
  supportsSmartRewinds,
  timezone,
  updateCurrentPlaylist,
  waveGraphBars,
  waveUnits,
  playing,
  userWantsRewindPlaying,
  currentTimestampRef,
  hasCurrentTimestamp,
  playerRef,
  isFullscreen,
  hasCamRewindPermissions,
  scrubberDayLabel,
  isPreviewActive,
  paywallActive,
  onTogglePlayPause,
}: Props) => {
  // normal refs don't update when the component re-renders and it has no width on the first render
  const [sliderRef, setSliderRef] = useState<HTMLElement | null>(null);

  const scrubStartTimestamp: MutableRefObject<number | null> = useRef(null);
  const scrubStartYPosition: MutableRefObject<number | null> = useRef(null);

  const { currentPlaylist, isCurrentPlaylistLatest, previouslyCompletedPlaylists } =
    useContext(RewindsContext);

  const { clientWidth: sliderWidth } = useClientSize(sliderRef, {
    observeWidth: true,
    observeHeight: false,
  });

  // calculate the number of milliseconds that the whole slider represents and add that to the startTime
  const totalSliderTime = useMemo(
    () => (bucketEndTimeInMs - bucketStartTimeInMs) / percentageRepresentingRewindBar,
    [bucketStartTimeInMs, bucketEndTimeInMs],
  );
  const sliderMaxTimeInMs = useMemo(
    () => bucketStartTimeInMs + totalSliderTime,
    [bucketStartTimeInMs, totalSliderTime],
  );

  // to keep the button within the slider rather than hanging off the end of it
  const sliderEndTimeInMs = useMemo(
    () =>
      sliderWidth > 0
        ? sliderMaxTimeInMs - (halfSliderThumbWidth / sliderWidth) * totalSliderTime
        : sliderMaxTimeInMs,
    [sliderWidth, totalSliderTime, sliderMaxTimeInMs],
  );

  // Keeps track of if the slider is currently being dragged
  const [changingSliderValue, setChangingSliderValue] = useState(false);
  const [camAdRunning, setCamAdRunning] = useCamAdRunning();

  const { hasAdFreeCamPermissions } = useUserPermissionStatus();

  const forceHideCamHostAd =
    multiCamViewActive || (currentPreviewPlayer === Player.REWIND && !showCamHostInRewind);

  const { controlsVisible, showCamHostAd } = useCamControls({
    playerEventTriggerEl: playerRef,
    isScrubbing: changingSliderValue,
    persistControlsOnFirstLoad: !hasAdFreeCamPermissions || currentPreviewPlayer === Player.REWIND,
    camId: camera._id,
    forceHideControls: multiCamViewActive || paywallActive || !!isPreviewActive,
    forceHideCamHostAd,
  });

  const setScrubbing = useCallback(
    (isScrubbing: boolean) => {
      setChangingSliderValue(isScrubbing);
      onScrub(isScrubbing);
    },
    [onScrub],
  );

  const isOutOfRange = useMemo(() => {
    const isLight = currentPlaylist?.rewinds.some(
      ({ mlStatus, insights }) =>
        mlStatus === 'COMPLETE' && !insights?.isVisibilityBad && !insights?.lowWaveCount,
    );
    return !isLight && supportsSmartRewinds;
  }, [currentPlaylist, supportsSmartRewinds]);

  const previouslyCompletedPlaylist = useMemo(() => {
    const formattedTime = formatInTimeZone(currentPlaylist?.bucketEnd ?? 0, timezone, 'HH');
    const startHour = parseInt(formattedTime, 10);
    const isMorning = startHour < 12;
    return isMorning ? previouslyCompletedPlaylists?.morning : previouslyCompletedPlaylists?.night;
  }, [currentPlaylist, previouslyCompletedPlaylists, timezone]);

  const outOfRangeClick = useCallback(() => {
    if (previouslyCompletedPlaylist?.bucketEnd) {
      updateCurrentPlaylist(previouslyCompletedPlaylist.bucketEnd.toISOString());
      trackEvent('Played Clip', {
        button: 'Wave Timeline Too Dark CTA',
        camId: camera?._id,
        camName: camera?.alias,
        category: 'KBYG Rewinds',
        clipStartTime: previouslyCompletedPlaylist.bucketStart.toISOString(),
        daysAgo: Math.round(
          (Date.now() - previouslyCompletedPlaylist.bucketStart.getTime()) / (1000 * 60 * 60 * 24),
        ),
        lastTrackedWaves: true,
        pageName: 'KBYG Rewinds',
        premiumCam: camera?.isPremium,
        smartCam: supportsSmartRewinds,
        lineupCam: !!camera?.isLineupCam,
        spotId: spot?._id,
        spotName: spot?.name,
      });
    }
  }, [camera, previouslyCompletedPlaylist, spot, supportsSmartRewinds, updateCurrentPlaylist]);

  const timelineClass = cx({
    [styles.timeline]: true,
    [styles.isLive]: currentPreviewPlayer === Player.LIVE,
    [styles.previewDisabled]: isPreviewActive,
  });

  const wrapperClass = cx({
    [styles.scrubber]: true,
    [styles.hasInsights]:
      hasInsights &&
      supportsSmartRewinds &&
      !multiCamViewActive &&
      spot.insightsCameraId &&
      showRewindGraph &&
      hasRewinds,
    [styles.isOutOfBounds]:
      isOutOfRange &&
      supportsSmartRewinds &&
      !multiCamViewActive &&
      spot.insightsCameraId &&
      showRewindGraph &&
      hasRewinds,
    [styles.hasOnlyErrors]: !hasInsights && errors?.length > 0,
    [styles.supportsSmartRewinds]:
      supportsSmartRewinds &&
      !multiCamViewActive &&
      spot.insightsCameraId &&
      showRewindGraph &&
      hasRewinds,
    [styles.multiCamViewActive]: multiCamViewActive,
    [styles.camAdRunning]: camAdRunning,
  });

  const clipStartTime = useMemo(() => {
    const startTime = new Date(bucketStartTimeInMs);
    startTime.setMinutes(0, 0, 0);

    return formatInTimeZone(startTime, timezone, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
  }, [bucketStartTimeInMs, timezone]);

  const shouldTrackScrubEvent = useRef(true);
  const trackCamScrubbedDebounced = useMemo(
    () =>
      debounce(
        (eventData) => {
          /*
        There's a bug with the MUI Slider where tapping on mobile can fire
        the onChange and onChangeCommitted events multiple times.
        Debouncing ensures that we only track the event once within 2 seconds
        and this will also help ensure we don't overtrack.
        More info here: https://github.com/mui/material-ui/issues/31869
        */
          if (shouldTrackScrubEvent.current) trackEvent('Cam Rewind Scrubbed', eventData);
          shouldTrackScrubEvent.current = false;
        },
        2000,
        { leading: true, trailing: false },
      ),
    [],
  );

  const handleSliderOnChangeCommitted = useCallback(
    (event: Event | SyntheticEvent<Element, Event>, value: number | number[]) => {
      if (camAdRunning || multiCamViewActive || !hasRewinds) return;

      if ((value as number) >= bucketEndTimeInMs) {
        // When live always set the value to be the max value so it snaps to the end
        setSliderTimeValue(sliderEndTimeInMs);
        showLive();
      } else {
        // if live is currently committed - we are coming from live state to rewind state
        const forcePlay = currentPlayer === Player.LIVE && hasCamRewindPermissions;
        setPlayerTime(value as number, userWantsRewindPlaying, forcePlay);
        showRewind();
      }
      setScrubbing(false);

      let scrubMethod;
      const clientY = scrubStartYPosition.current;
      const sliderY = sliderRef?.getBoundingClientRect()?.top ?? null;
      if (isFinite(sliderY) && isFinite(clientY) && sliderY !== null && clientY !== null) {
        scrubMethod = clientY - sliderY < TIMELINE_VIRTUAL_SCRUB_HEIGHT ? 'timeline' : 'graph';
      }
      trackCamScrubbedDebounced({
        isCurrentClip: isCurrentPlaylistLatest,
        isLiveTarget: (value as number) >= bucketEndTimeInMs,
        isLiveSource: currentPlayer === Player.LIVE,
        premiumCam: camera.isPremium,
        smartCam: !!(supportsSmartRewinds && spot.insightsCameraId),
        clipStartTime,
        camId: camera._id,
        camName: camera.alias,
        spotId: spot?._id,
        spotName: spot?.name,
        subregionId: spot?.subregion?._id,
        scrubMethod,
        scrubDragged: scrubStartTimestamp.current !== null && scrubStartTimestamp.current !== value,
      });
      scrubStartTimestamp.current = null;
      scrubStartYPosition.current = null;
    },
    [
      currentPlayer,
      camAdRunning,
      bucketEndTimeInMs,
      setPlayerTime,
      showLive,
      showRewind,
      setScrubbing,
      sliderRef,
      sliderEndTimeInMs,
      userWantsRewindPlaying,
      hasCamRewindPermissions,
      trackCamScrubbedDebounced,
      setSliderTimeValue,
      multiCamViewActive,
      spot,
      camera,
      clipStartTime,
      supportsSmartRewinds,
      isCurrentPlaylistLatest,
      hasRewinds,
    ],
  );

  // Keeps internal state of slider timer value which we use to decide if we want to update the player based on if that time is live or rewind
  const handleSliderOnChange = useCallback(
    (event: Event, value: number | number[]) => {
      if (camAdRunning || multiCamViewActive || !hasRewinds) return;

      // Casting here as we always know the value we set is just a number not an array of numbers
      setScrubbing(true);
      setSliderTimeValue(Number(value as number));

      if (!isFinite(scrubStartTimestamp.current)) {
        // save the start timestamp so we can tell if they just clicked or if they scrubbed
        scrubStartTimestamp.current = value as number;
      }
      if (!isFinite(scrubStartYPosition.current)) {
        // save the Y position so we can tell if they clicked the graph or the timeline
        scrubStartYPosition.current =
          (event as TouchEvent)?.touches?.[0]?.clientY ?? (event as MouseEvent)?.clientY;
      }

      if ((value as number) >= bucketEndTimeInMs || multiCamViewActive || !hasRewinds) {
        showPreviewOfLive();
      } else {
        showPreviewOfRewind();
      }
    },
    [
      bucketEndTimeInMs,
      showPreviewOfLive,
      showPreviewOfRewind,
      setScrubbing,
      setSliderTimeValue,
      multiCamViewActive,
      camAdRunning,
      hasRewinds,
    ],
  );

  const valueLabelFormat = useCallback(
    (value: number) => {
      if (!sliderTimeValue || (sliderTimeValue && sliderTimeValue > bucketEndTimeInMs)) {
        return 'Live';
      }
      if (scrubberDayLabel) {
        return `${scrubberDayLabel} ${formatInTimeZone(value, timezone, 'h:mm:ssaaa')}`;
      }
      return formatInTimeZone(value, timezone, 'h:mm:ssaaa');
    },
    [bucketEndTimeInMs, scrubberDayLabel, sliderTimeValue, timezone],
  );

  // This ensures that if the camera id changes we always reset state for the new one so
  // users can scroll while on a paywall. If we don't reset then an ad may still be playing on
  // a previous camera which will lock the user from being able to scroll.
  useEffect(() => {
    setCamAdRunning(false);
  }, [camera._id, setCamAdRunning]);

  useEffect(() => {
    if (currentPlayer === Player.LIVE && !changingSliderValue) {
      setSliderTimeValue(sliderEndTimeInMs);
    }
  }, [currentPlayer, sliderEndTimeInMs, changingSliderValue, setSliderTimeValue]);

  // Updates the slider value when the player time changes so it ticks along
  useEffect(() => {
    if (!changingSliderValue && currentPlayer === Player.REWIND && playerTime) {
      setSliderTimeValue(playerTime);
    }
  }, [playerTime, changingSliderValue, currentPlayer, setSliderTimeValue]);

  useCalculateSliderPillMargin(
    sliderRef,
    playerWidth,
    bucketStartTimeInMs,
    bucketEndTimeInMs,
    sliderTimeValue,
  );

  const disabledSliderCopy = useMemo(() => {
    if (multiCamViewActive) {
      return 'Switch to Single Cam Mode to activate Rewind functionality.';
    }

    return '';
  }, [multiCamViewActive]);

  const highlightedBarIndex = useMemo(
    () =>
      isNumber(sliderTimeValue)
        ? Math.floor((sliderTimeValue - bucketStartTimeInMs) / GRAPH_BAR_WIDTH_IN_MS)
        : 0,
    [sliderTimeValue, bucketStartTimeInMs],
  );

  return (
    <div className={wrapperClass} data-testid="kbyg-rewinds-scrubber">
      {!multiCamViewActive && (
        <>
          <CamPlayerScrubberControls
            playing={playing}
            controlsVisible={controlsVisible}
            setPlayerTime={setPlayerTime}
            userWantsRewindPlaying={userWantsRewindPlaying}
            currentTimestampRef={currentTimestampRef}
            hasCurrentTimestamp={hasCurrentTimestamp}
            playerRef={playerRef}
            currentPreviewPlayer={currentPreviewPlayer}
            hasCamRewindPermissions={hasCamRewindPermissions}
            onTogglePlayPause={onTogglePlayPause}
          />
          <div className={cx(styles.controlsBackdrop, controlsVisible && styles.show)} />
          <div
            className={cx(
              styles.camHostLinkContainer,
              showCamHostAd && !controlsVisible && styles.show,
              controlsVisible && styles.controlsVisible,
              !showCamHostAd && styles.hide,
              showCamHostInRewind && styles.showCamHostInRewind,
            )}
          >
            <CamHostAd spot={spot} camera={camera} />
          </div>
          {sliderTimeValue && (
            <div
              className={cx(
                styles.fixedTimeLabel,
                !controlsVisible && styles.show,
                controlsVisible && styles.controlsVisible,
                currentPreviewPlayer !== Player.REWIND && styles.hide,
              )}
            >
              <Typography variant="callout2" color={theme.palette.common.white}>
                {formatInTimeZone(sliderTimeValue, timezone, 'h:mm:ssaaa')}
              </Typography>
            </div>
          )}
        </>
      )}
      <div className={timelineClass} data-testid="kbyg-rewinds-scrubber--timeline">
        {multiCamViewActive && (
          <div>
            <Typography
              variant="callout2"
              className={styles.multiCamModal}
              data-testid="multicam-scrubber-modal"
            >
              {disabledSliderCopy}
            </Typography>
          </div>
        )}

        <Slider
          data-testid="kbyg-rewinds-scrubber--slider"
          className={cx(
            styles.slider,
            multiCamViewActive && styles.multiCamViewActive,
            controlsVisible && styles.controlsVisible,
          )}
          defaultValue={sliderEndTimeInMs}
          min={bucketStartTimeInMs}
          max={sliderMaxTimeInMs}
          value={sliderTimeValue || sliderEndTimeInMs}
          step={10}
          valueLabelDisplay="on"
          valueLabelFormat={valueLabelFormat}
          onChange={handleSliderOnChange}
          onChangeCommitted={handleSliderOnChangeCommitted}
          ref={setSliderRef}
          aria-label="Scrub through the timeline to rewind or fast forward"
        />
        <div
          className={cx(
            styles.contentIndicator,
            controlsVisible && styles.controlsVisible,
            multiCamViewActive && styles.multiCamViewActive,
          )}
        >
          <div className={styles.axisBuffer} />
          <div className={styles.rewind} />
          <div className={styles.live} data-testid="kbyg-rewinds-scrubber--live-indicator" />
        </div>
        {showRewindGraph && !!sliderTimeValue && !multiCamViewActive && hasRewinds && (
          <div className={styles.waveGraph} data-testid="cam-rewinds-wave-bar-graph-wrapper">
            <WaveBarGraph
              bucketEndTimeInMs={bucketEndTimeInMs}
              bucketStartTimeInMs={bucketStartTimeInMs}
              errors={errors}
              highlightedBarIndex={highlightedBarIndex}
              isOutOfRange={isOutOfRange}
              outOfRangeClick={previouslyCompletedPlaylist ? outOfRangeClick : undefined}
              supportsSmartRewinds={!!(supportsSmartRewinds && spot.insightsCameraId) || false}
              timezone={timezone}
              waveGraphBars={waveGraphBars}
              waveUnits={waveUnits}
              isFullscreen={isFullscreen}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CamPlayerRewindsScrubber;
